import React, { useState } from 'react';
import {
  ShieldCheckIcon, ShieldExclamationIcon, LockClosedIcon,
  KeyIcon, EyeIcon, ArrowPathIcon, ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useQuery, usePagination, useDebounce } from '../../hooks';
import { adminAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, Badge, StatusBadge,
  Modal, Select, Pagination, LoadingPage, EmptyState
} from '../../components/common/UI';
import { format } from 'date-fns';

export default function SecurityAndAuditCenter() {
  const [activeTab, setActiveTab] = useState('audit_logs');
  const [search, setSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const debouncedSearch = useDebounce(search, 300);
  const { page, setPage } = usePagination();

  // ── Audit Logs Query ────────────────────────────────
  const { data: auditData, loading: auditLoading, refetch: refetchAudit } = useQuery(
    () => adminAPI.auditLogs({ page, limit: 15, search: debouncedSearch }),
    [page, debouncedSearch, activeTab],
    { enabled: activeTab === 'audit_logs' }
  );

  // ── Security Events Query ───────────────────────────
  const { data: securityData, loading: securityLoading, refetch: refetchSecurity } = useQuery(
    () => adminAPI.securityEvents({ page, limit: 15 }),
    [page, activeTab],
    { enabled: activeTab === 'events' }
  );

  // ── Failed Logins Query ─────────────────────────────
  const { data: failedData, loading: failedLoading, refetch: refetchFailed } = useQuery(
    () => adminAPI.failedLogins({ page, limit: 15 }),
    [page, activeTab],
    { enabled: activeTab === 'failed_logins' }
  );

  const logs = auditData?.rows || auditData?.logs || [];
  const totalLogs = auditData?.total || auditData?.count || 0;

  return (
    <div className="space-y-6 animate-in">
      <SectionHeader
        title="Security Center & Tamper-Resistant Audit Trail"
        subtitle="Immutable record of administrative mutations, security alarms, login telemetry and risk monitoring"
      />

      {/* Security Health Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-surface-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-surface-500 uppercase">Audit Records</span>
            <ShieldCheckIcon className="h-5 w-5 text-success-600" />
          </div>
          <p className="text-2xl font-bold text-surface-900 mt-2">{totalLogs}</p>
          <p className="text-[11px] text-surface-400 mt-0.5">Append-only immutable storage</p>
        </Card>

        <Card className="p-4 bg-white border border-surface-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-danger-600 uppercase">Failed Logins (24h)</span>
            <LockClosedIcon className="h-5 w-5 text-danger-600" />
          </div>
          <p className="text-2xl font-bold text-danger-700 mt-2">{failedData?.count_24h || 0}</p>
          <p className="text-[11px] text-danger-500 mt-0.5">Brute-force protection active</p>
        </Card>

        <Card className="p-4 bg-white border border-surface-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-warning-600 uppercase">Rate-Limit Hits</span>
            <ExclamationTriangleIcon className="h-5 w-5 text-warning-600" />
          </div>
          <p className="text-2xl font-bold text-warning-700 mt-2">{securityData?.rate_limit_hits || 0}</p>
          <p className="text-[11px] text-surface-400 mt-0.5">Automated firewall mitigation</p>
        </Card>

        <Card className="p-4 bg-white border border-surface-200">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-brand-600 uppercase">Active Admin Sessions</span>
            <KeyIcon className="h-5 w-5 text-brand-600" />
          </div>
          <p className="text-2xl font-bold text-brand-700 mt-2">{securityData?.active_admin_sessions || 1}</p>
          <p className="text-[11px] text-surface-400 mt-0.5">All sessions MFA authenticated</p>
        </Card>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex gap-1 rounded-xl bg-surface-100 p-1 w-full sm:w-auto">
          {[
            { key: 'audit_logs', label: '🛡️ Audit Trail' },
            { key: 'failed_logins', label: '🚨 Failed Logins' },
            { key: 'events', label: '⚡ Security Alarms' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1); }}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.key ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'audit_logs' && (
          <SearchInput
            value={search}
            onChange={(val) => { setSearch(val); setPage(1); }}
            placeholder="Search by action, admin email, entity ID..."
            className="w-full sm:w-80"
          />
        )}
      </div>

      {/* Audit Logs Table */}
      {activeTab === 'audit_logs' && (
        <Card className="p-0 overflow-hidden">
          {auditLoading ? (
            <LoadingPage />
          ) : logs.length === 0 ? (
            <EmptyState icon={ShieldCheckIcon} title="No audit records match query" description="All high-risk mutations and admin actions are recorded here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Actor / Admin</th>
                    <th>Target Entity</th>
                    <th>Severity</th>
                    <th>Result</th>
                    <th>Justification Reason</th>
                    <th className="text-right">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id || log.request_id}>
                      <td className="text-xs text-surface-500 whitespace-nowrap font-mono">
                        {log.created_at ? format(new Date(log.created_at), 'dd MMM yyyy, HH:mm:ss') : '—'}
                      </td>
                      <td>
                        <span className="font-mono font-bold text-xs text-brand-700 bg-brand-50 px-2 py-0.5 rounded border border-brand-200">
                          {log.action_type}
                        </span>
                      </td>
                      <td>
                        <div>
                          <p className="text-xs font-semibold text-surface-800">{log.admin_email || `Admin #${log.admin_id}`}</p>
                          <p className="text-[10px] font-mono text-surface-400">IP: {log.ip_address || '—'}</p>
                        </div>
                      </td>
                      <td>
                        <span className="text-xs font-medium text-surface-700">
                          {log.entity_type} #{log.entity_id}
                        </span>
                      </td>
                      <td>
                        <Badge variant={log.severity === 'CRITICAL' ? 'red' : log.severity === 'WARN' ? 'yellow' : 'blue'}>
                          {log.severity || 'INFO'}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={log.result === 'FAILURE' ? 'red' : 'green'}>
                          {log.result || 'SUCCESS'}
                        </Badge>
                      </td>
                      <td className="max-w-xs truncate text-xs text-surface-600 italic" title={log.reason}>
                        "{log.reason}"
                      </td>
                      <td className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={EyeIcon}
                          onClick={() => setSelectedLog(log)}
                        >
                          Inspect
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-4 border-t border-surface-100">
            <Pagination page={page} pages={Math.ceil(totalLogs / 15) || 1} onPageChange={setPage} />
          </div>
        </Card>
      )}

      {/* Failed Logins Tab */}
      {activeTab === 'failed_logins' && (
        <Card className="p-0 overflow-hidden">
          {failedLoading ? (
            <LoadingPage />
          ) : (failedData?.rows || []).length === 0 ? (
            <EmptyState icon={LockClosedIcon} title="Zero failed login alarms" description="No suspicious login attempts recorded." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Target Account</th>
                    <th>IP Address</th>
                    <th>Risk Signal</th>
                    <th>Failure Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(failedData?.rows || []).map((f) => (
                    <tr key={f.id}>
                      <td className="text-xs text-surface-500 font-mono">
                        {f.created_at ? format(new Date(f.created_at), 'dd MMM yyyy, HH:mm:ss') : '—'}
                      </td>
                      <td className="text-xs font-semibold text-surface-900">{f.email || f.username}</td>
                      <td className="text-xs font-mono text-surface-600">{f.ip_address}</td>
                      <td>
                        <Badge variant="red">{f.risk_level || 'SUSPICIOUS_IP'}</Badge>
                      </td>
                      <td className="text-xs text-danger-600">{f.reason || 'Invalid credentials'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Security Alarms Tab */}
      {activeTab === 'events' && (
        <Card className="p-0 overflow-hidden">
          {securityLoading ? (
            <LoadingPage />
          ) : (securityData?.rows || []).length === 0 ? (
            <EmptyState icon={ShieldExclamationIcon} title="No security events detected" description="Platform security firewall operating normally." />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Event Code</th>
                    <th>Source IP</th>
                    <th>Severity</th>
                    <th>Automated Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(securityData?.rows || []).map((e) => (
                    <tr key={e.id}>
                      <td className="text-xs text-surface-500 font-mono">
                        {e.created_at ? format(new Date(e.created_at), 'dd MMM yyyy, HH:mm:ss') : '—'}
                      </td>
                      <td className="text-xs font-mono font-bold text-surface-900">{e.event_type}</td>
                      <td className="text-xs font-mono text-surface-600">{e.ip_address}</td>
                      <td>
                        <Badge variant={e.severity === 'CRITICAL' ? 'red' : 'yellow'}>{e.severity}</Badge>
                      </td>
                      <td className="text-xs text-surface-700">{e.action_taken || 'IP Rate-limited for 15m'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Audit Log Inspect Modal */}
      {selectedLog && (
        <Modal
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Audit Trail Record Details"
          size="lg"
        >
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-3 bg-surface-50 p-3 rounded-xl border border-surface-200">
              <div>
                <p className="text-surface-400 font-semibold uppercase text-[10px]">Request ID</p>
                <p className="font-mono text-surface-800 break-all">{selectedLog.request_id || '—'}</p>
              </div>
              <div>
                <p className="text-surface-400 font-semibold uppercase text-[10px]">Correlation ID</p>
                <p className="font-mono text-surface-800 break-all">{selectedLog.correlation_id || '—'}</p>
              </div>
              <div>
                <p className="text-surface-400 font-semibold uppercase text-[10px]">Actor Email / ID</p>
                <p className="font-semibold text-surface-900">{selectedLog.admin_email} (ID: {selectedLog.admin_id})</p>
              </div>
              <div>
                <p className="text-surface-400 font-semibold uppercase text-[10px]">Timestamp & Client IP</p>
                <p className="font-mono text-surface-800">
                  {selectedLog.created_at ? format(new Date(selectedLog.created_at), 'dd MMM yyyy, HH:mm:ss') : '—'} | IP: {selectedLog.ip_address}
                </p>
              </div>
            </div>

            <div className="bg-surface-50 p-3 rounded-xl border border-surface-200">
              <p className="text-surface-400 font-semibold uppercase text-[10px]">Justification Reason</p>
              <p className="text-surface-900 font-medium mt-0.5">"{selectedLog.reason}"</p>
            </div>

            {/* Redacted Old & New Value Diff */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="font-bold text-surface-700 mb-1">Previous State (Sanitized)</p>
                <pre className="bg-surface-900 text-surface-100 p-3 rounded-xl overflow-x-auto text-[11px] font-mono max-h-48">
                  {JSON.stringify(selectedLog.old_values || {}, null, 2)}
                </pre>
              </div>
              <div>
                <p className="font-bold text-surface-700 mb-1">New State (Sanitized)</p>
                <pre className="bg-surface-900 text-brand-300 p-3 rounded-xl overflow-x-auto text-[11px] font-mono max-h-48">
                  {JSON.stringify(selectedLog.new_values || {}, null, 2)}
                </pre>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" size="sm" onClick={() => setSelectedLog(null)}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
